import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { get, set, del } from 'idb-keyval';
import { IonApp, setupIonicReact } from '@ionic/react';
import { HelmetProvider } from 'react-helmet-async';
import { ConnectPage, CallbackPage } from './pages';
import LogoutIframePage from './pages/LogoutIframePage/LogoutIframePage';
import ProjectsPage from './pages/ProjectsPage/ProjectsPage';
import ProjectLayout from './pages/ProjectsPage/ProjectLayout';
import ProjectRunsList from './pages/ProjectsPage/ProjectRunsList';
import PipelinesPage from './pages/PipelinesPage/PipelinesPage';
import PipelineDetailPage from './pages/PipelinesPage/PipelineDetailPage';
import AppsPage from './pages/AppsPage/AppsPage';
import AppDetailPage from './pages/AppsPage/AppDetailPage';
import StoragePage from './pages/StoragePage/StoragePage';
import StoreDetailPage from './pages/StoragePage/StoreDetailPage';
import MarketplacePage from './pages/MarketplacePage/MarketplacePage';
import AppsMarketplacePage from './pages/AppsMarketplacePage/AppsMarketplacePage';
import TicketsPage from './pages/TicketsPage/TicketsPage';
import TicketDetailsPage from './pages/TicketDetailsPage/TicketDetailsPage';
import SentryIssuesPage from './pages/SentryIssuesPage/SentryIssuesPage';
import SentryIssueDetailsPage from './pages/SentryIssueDetailsPage/SentryIssueDetailsPage';
import ProjectSettings from './pages/ProjectSettings/ProjectSettings';
import ExecutionDetailsPageWrapper from './pages/ExecutionDetailsPage/ExecutionDetailsPageWrapper';
import ImplementationDetailsPageWrapper from './pages/ImplementationDetailsPage/ImplementationDetailsPageWrapper';
import './styles/execution-artifacts.css';
import InvitePage from './pages/InvitePage/InvitePage';
import IntegrationsPage from './pages/IntegrationsPage/IntegrationsPage';
import ContactPage from './pages/ContactPage/ContactPage';
import BillingPage from './pages/BillingPage/BillingPage';
import UsagePage from './pages/UsagePage/UsagePage';
import PrivacyPage from './pages/PrivacyPage/PrivacyPage';
import SecurityPage from './pages/SecurityPage/SecurityPage';
import TermsPage from './pages/TermsPage/TermsPage';
import SubProcessorsPage from './pages/SubProcessorsPage/SubProcessorsPage';
import PricingPage from './pages/PricingPage/PricingPage';
import DownloadPage from './pages/DownloadPage/DownloadPage';
import SolutionsPage from './pages/SolutionsPage/SolutionsPage';
import SlackPage from './pages/SlackPage/SlackPage';
import WorkflowEnginePage from './pages/SolutionPages/WorkflowEnginePage';
import AppsSolutionPage from './pages/SolutionPages/AppsPage';
import BrowserTestingPage from './pages/SolutionPages/BrowserTestingPage';
import CodeAnalysisPage from './pages/SolutionPages/CodeAnalysisPage';
import FirewalledReposPage from './pages/SolutionPages/FirewalledReposPage';
import AnalysisDiffDemo from './pages/DemoPage/AnalysisDiffDemo';
import CodeDiffDemo from './pages/DemoPage/CodeDiffDemo';
import SentryCrashDemo from './pages/DemoPage/SentryCrashDemo';
import CliAuthorizePage from './pages/CliAuthorizePage/CliAuthorizePage';
import TokensPage from './pages/TokensPage/TokensPage';
import ArtifactViewPage, { UiArtifactRedirect } from './pages/ArtifactViewPage/ArtifactViewPage';
import { ProtectedRoute } from './hoc';
import { ToastProvider } from 'contexts/ToastContext';
import { Navigate } from 'react-router-dom';
import { isSelfHost } from './utils/selfHost';
import './App.css';
import './i18n'; // Initialize i18n

// Self-host builds serve ONLY the operator dashboard (projects + agents). The
// SaaS-only surfaces (pricing/download/solutions/legal/contact/public
// marketplace/OAuth callbacks/demos/billing/usage) don't exist there and some
// pull cloud-only providers (Stripe), so we don't even register their routes.
// Cloud path is unchanged when the flag is off.
const SELF_HOST = isSelfHost();

// Create a context for chat toggle
export const ChatContext = React.createContext();

// Initialize Ionic
setupIonicReact({
  mode: 'md' // Use Material Design mode for web
});

// Create IndexedDB persister for React Query cache
const persister = {
  persistClient: async (client) => {
    await set('REACT_QUERY_OFFLINE_CACHE', client);
  },
  restoreClient: async () => {
    return await get('REACT_QUERY_OFFLINE_CACHE');
  },
  removeClient: async () => {
    await del('REACT_QUERY_OFFLINE_CACHE');
  },
};

// Create a client for React Query with IndexedDB persistence
// THE single React Query client for the whole app (provided below via
// PersistQueryClientProvider). The cache is PERSISTED to storage with a 24h
// gcTime, so it survives reloads and paints instantly from disk.
//
// staleTime: 0 is deliberate and load-bearing with that persistence —
// "instant paint from the persisted cache, then revalidate on mount" (the
// stale-while-revalidate pattern). A LONG per-query staleTime is actively
// HARMFUL here: it strands stale/persisted data for up to 24h and a refresh
// can't clear it (exactly what made a just-deployed agent show a stale
// 'not deployed' page). Keep per-query overrides SHORT (seconds); pair
// list/detail with poll-while-transitional + invalidate-on-mutation.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 60 * 24, // 24h — persisted cache lifetime
      staleTime: 0, // revalidate on mount (see note above — do NOT raise globally)
    },
  },
});

/**
 * Global method to clear all cache (React Query + IndexedDB + localStorage)
 * Usage: Open console and run: clearAllCache()
 */
window.clearAllCache = async () => {
  console.log('🗑️ Clearing all caches...');
  
  // Clear React Query cache
  queryClient.clear();
  console.log('✅ Cleared React Query cache');
  
  // Clear IndexedDB persisted cache
  await persister.removeClient();
  console.log('✅ Cleared IndexedDB cache');
  
  console.log('🎉 All caches cleared! Reloading page...');
  window.location.reload();
};

export const routesPath = {
  root: '/',
  dashboard: '/dashboard',
  projects: '/projects',
  connect: '/connect',
  billing: '/billing',
  usage: '/usage',
  contact: '/contact',
  pricing: '/pricing',
  download: '/download',
  solutions: '/solutions',
  slack: '/slack',
  privacy: '/privacy',
  security: '/security',
  terms: '/terms',
  subProcessors: '/sub-processors',
  settings: '/settings',
  integrations: '/integrations',
  googleCallback: '/google/callback',
  azureCallback: '/azure/callback',
};

function App() {
  // Initialize chat state from localStorage
  const [isChatOpen, setIsChatOpen] = useState(() => {
    const saved = localStorage.getItem('chatWidgetOpen');
    return saved ? JSON.parse(saved) : false;
  });

  // Persist chat open state
  useEffect(() => {
    localStorage.setItem('chatWidgetOpen', JSON.stringify(isChatOpen));
  }, [isChatOpen]);

  // Reveal app after React mounts to prevent FOUC
  useEffect(() => {
    document.getElementById('root')?.classList.add('ready');
  }, []);

  // Global keyboard shortcut: Cmd+B (Mac) or Ctrl+B (Windows) to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+B (Mac) or Ctrl+B (Windows/Linux)
      // Accept both lowercase 'b' and uppercase 'B'
      if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'B')) {
        console.log('🔥 Cmd+B detected! Toggling chat...');
        e.preventDefault(); // Prevent browser default (bookmark)
        e.stopPropagation(); // Stop event from bubbling
        
        // Toggle chat open/close and ensure it's in sidebar mode
        setIsChatOpen(prev => {
          const newState = !prev;
          console.log(`📊 Chat is now: ${newState ? 'OPEN' : 'CLOSED'}`);
          
          // If opening, ensure it's in sidebar mode
          if (newState) {
            localStorage.setItem('chatWidgetViewMode', 'sidebar');
            window.dispatchEvent(new CustomEvent('viewModeChange', { detail: 'sidebar' }));
          }
          
          return newState;
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  const toggleChat = () => setIsChatOpen(prev => !prev);

  return (
    <HelmetProvider>
      <IonApp>
        <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
          <ChatContext.Provider value={{ isChatOpen, toggleChat }}>
            <Router>
            <div className="App">
              <div className="app-main-content">
                <Routes>
                  {/* ==================== GROUP 1: PUBLIC (zibby.app) ==================== */}
                  {/* SaaS-only public surfaces — NOT registered in self-host
                      (single-origin operator dashboard, no marketing/legal/
                      OAuth/demo pages, no cloud providers). */}
                  {!SELF_HOST && (<>
                  {/* Cross-domain logout target — see AuthService.logout
                      and LogoutIframePage for why this exists. */}
                  <Route path="/logout-iframe" element={<LogoutIframePage />} />
                  <Route path={routesPath.connect} element={<ConnectPage />} />
                  {/* Public marketplace catalog — same component as the
                      project-scoped /projects/:id/marketplace, but renders
                      without a project context. Browse + view detail work
                      anonymously; clicking Deploy bounces to login → project
                      picker. */}
                  <Route path="/marketplace" element={<MarketplacePage />} />
                  {/* Managed Apps marketplace — public browse, deploy
                      requires sign-in (modal bounces to /signup). */}
                  <Route path="/marketplace/apps" element={<AppsMarketplacePage />} />
                  <Route path={routesPath.pricing} element={<PricingPage />} />
                  <Route path={routesPath.download} element={<DownloadPage />} />
                  <Route path={routesPath.solutions} element={<SolutionsPage />} />
                  <Route path="/solutions/workflow" element={<WorkflowEnginePage />} />
                  <Route path="/solutions/apps" element={<AppsSolutionPage />} />
                  <Route path="/solutions/browser-testing" element={<BrowserTestingPage />} />
                  <Route path="/solutions/code-analysis" element={<CodeAnalysisPage />} />
                  <Route path="/solutions/firewalled-repos" element={<FirewalledReposPage />} />
                  {/* PUBLIC Slack app landing page — must NOT be behind login.
                      Slack Marketplace review requires a publicly reachable page
                      describing the app, its install steps, what it responds to,
                      and a visible link to the privacy policy. */}
                  <Route path={routesPath.slack} element={<SlackPage />} />
                  <Route path={routesPath.privacy} element={<PrivacyPage />} />
                  <Route path={routesPath.security} element={<SecurityPage />} />
                  <Route path={routesPath.terms} element={<TermsPage />} />
                  <Route path={routesPath.subProcessors} element={<SubProcessorsPage />} />
                  {/* PUBLIC support/contact page — must NOT be behind login (Slack
                      Marketplace requires a publicly accessible support page). Wrapped
                      in its OWN ToastProvider: the app-wide ToastProvider lives inside
                      the authenticated layout, so a public render would otherwise crash
                      on useToast (blank page). */}
                  <Route path={routesPath.contact} element={<ToastProvider><ContactPage /></ToastProvider>} />
                  <Route path={routesPath.googleCallback} element={<CallbackPage provider="google" />} />
                  <Route path={routesPath.azureCallback} element={<CallbackPage provider="microsoft" />} />
                  <Route path={routesPath.facebookCallback} element={<CallbackPage provider="facebook" />} />
                  <Route path={routesPath.calendarCallback} element={<CallbackPage provider="google" />} />
                  <Route path="/demo/analysis-diff" element={<AnalysisDiffDemo />} />
                  <Route path="/demo/code-diff" element={<CodeDiffDemo />} />
                  <Route path="/demo/sentry-crash" element={<SentryCrashDemo />} />
                  </>)}

                  {/* ==================== GROUP 2: AUTHENTICATED (studio.zibby.app) ==================== */}
                  <Route path={routesPath.root} element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
                  <Route path={routesPath.projects} element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
                  <Route path={routesPath.integrations} element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
                  {/* Billing/usage/contact are cloud-only (Stripe / SaaS
                      account) — not registered in self-host. */}
                  {!SELF_HOST && (<>
                  <Route path={routesPath.billing} element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
                  {/* Tab-keyed entry for the billing page — e.g.
                      `/billing/credits` (Stripe-return for credit
                      top-ups), `/billing/invoices`. Same component;
                      reads `:tab` via useParams() to pick initial tab. */}
                  <Route path={`${routesPath.billing}/:tab`} element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
                  <Route path={routesPath.usage} element={<ProtectedRoute><UsagePage /></ProtectedRoute>} />
                  </>)}
                  <Route path="/settings/tokens" element={<ProtectedRoute><TokensPage /></ProtectedRoute>} />
                  {/* Member-gated viewer for one published artifact (the
                      artifact skill's shareable pages). Login here; project
                      membership is enforced server-side by the authed
                      GET /credits/artifacts/:id. */}
                  <Route path="/artifact/:id" element={<ProtectedRoute><ArtifactViewPage /></ProtectedRoute>} />
                  {/* Compat: the backend mints share URLs as
                      `<base>/ui#/artifact/<id>` (self-host dashboard shape);
                      cloud has no /ui, so translate the hash form onto
                      /artifact/:id instead of bouncing to /. */}
                  <Route path="/ui" element={<UiArtifactRedirect />} />
                  <Route path="/authorize" element={<CliAuthorizePage />} />
                  <Route path="/invite/:token" element={<InvitePage />} />
                  <Route path="/projects/:projectId/runs/:executionId" element={<ExecutionDetailsPageWrapper />} />
                  <Route path="/projects/:projectId/implementations/:executionId" element={<ProtectedRoute><ImplementationDetailsPageWrapper /></ProtectedRoute>} />
                  <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectLayout /></ProtectedRoute>}>
                    <Route index element={<Navigate to="agents" replace />} />
                    {/* `agents` is the canonical URL segment; the legacy
                        `pipelines` paths are kept as aliases (same element)
                        so existing bookmarks/deep-links don't 404. */}
                    <Route path="agents" element={<PipelinesPage />} />
                    <Route path="pipelines" element={<PipelinesPage />} />
                    <Route path="agents/:workflowType" element={<PipelineDetailPage />} />
                    <Route path="pipelines/:workflowType" element={<PipelineDetailPage />} />
                    {/* Run detail is rendered INLINE inside the workflow
                        detail page's Activity tab, not as a separate
                        page. Keeps the URL deep-linkable AND keeps the
                        project sidebar + breadcrumb working (the
                        standalone run page had its own broken nav). */}
                    <Route path="agents/:workflowType/runs/:executionId" element={<PipelineDetailPage />} />
                    <Route path="pipelines/:workflowType/runs/:executionId" element={<PipelineDetailPage />} />
                    <Route path="testruns" element={<ProjectRunsList />} />
                    <Route path="apps" element={<AppsPage />} />
                    {/* Solo (dedicated-EC2) apps are keyed by appSlug, not
                        instanceId, and load from the solo status endpoint.
                        Declared BEFORE the :instanceId catch-all so `solo`
                        isn't swallowed as an instance id. */}
                    {/* Storage & Database — the project's stores (databases /
                        data stores). List → per-store DETAIL page (viewer +
                        query + delete). Creation stays conversational
                        (agent / Copilot), so there is no create form. */}
                    <Route path="stores" element={<StoragePage />} />
                    <Route path="stores/:storeId" element={<StoreDetailPage />} />
                    {/* Managed-apps + hosted-marketplace + ticket/issue
                        integrations are cloud-only surfaces — not registered
                        in self-host. */}
                    {!SELF_HOST && (<>
                    <Route path="apps/solo/:slug" element={<AppDetailPage />} />
                    <Route path="apps/:instanceId" element={<AppDetailPage />} />
                    <Route path="marketplace" element={<MarketplacePage />} />
                    {/* `marketplace/agents` is canonical; `marketplace/workflows`
                        kept as an alias so old links don't 404. */}
                    <Route path="marketplace/agents" element={<MarketplacePage />} />
                    <Route path="marketplace/workflows" element={<MarketplacePage />} />
                    <Route path="marketplace/apps" element={<AppsMarketplacePage />} />
                    <Route path="tickets" element={<TicketsPage />} />
                    <Route path="tickets/:ticketKey" element={<TicketDetailsPage />} />
                    <Route path="issues" element={<SentryIssuesPage />} />
                    <Route path="issues/:issueId" element={<SentryIssueDetailsPage />} />
                    </>)}
                    <Route path="settings" element={<ProjectSettings />} />
                  </Route>

                  {/* Legacy redirects + OAuth callbacks + demos are cloud-only. */}
                  {!SELF_HOST && (<>
                  {/* Legacy Calendar Routes - redirect to integrations */}
                  <Route path={routesPath.calendarSettings} element={<Navigate to={routesPath.integrations} replace />} />
                  <Route path={routesPath.settings} element={<Navigate to={routesPath.integrations} replace />} />

                  {/* OAuth Callback Routes */}
                  <Route path={routesPath.googleCallback} element={<CallbackPage provider="google" />} />
                  <Route path={routesPath.azureCallback} element={<CallbackPage provider="microsoft" />} />
                  <Route path={routesPath.facebookCallback} element={<CallbackPage provider="facebook" />} />
                  <Route path={routesPath.calendarCallback} element={<CallbackPage provider="google" />} /> {/* Legacy support */}

                  {/* Demo Routes */}
                  <Route path="/demo/analysis-diff" element={<AnalysisDiffDemo />} />
                  <Route path="/demo/code-diff" element={<CodeDiffDemo />} />
                  <Route path="/demo/sentry-crash" element={<SentryCrashDemo />} />
                  </>)}

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
              
            </div>
            </Router>
          </ChatContext.Provider>
        </PersistQueryClientProvider>
      </IonApp>
    </HelmetProvider>
  );
}

export default App;
